import "../helpers/publicPath";
import React, { useReducer, Suspense, lazy } from "react";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import { theme } from "../theme";
import { NavView } from "../features/nav/NavView";
import { RenderKind } from "../types/render-kind";
import { CartProvider } from "../features/cart";
import { PdpFlow, PdpFlowProps } from "../modules/PdpFlow";
import { initSentry } from "../helpers/errors";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "../helpers/fetch";
import { Customer, CustomerProvider } from "../features/CustomerContext";
import { GrowthBookProvider } from "@growthbook/growthbook-react";
import { initializeAnnouncementBar } from "../modules/announcement-bar";
import { getProduct, trimPortalProps } from "../helpers/droppsApp";
import { growthBook } from "../helpers/growthbook";

const UserTestimonialModule = lazy(() =>
  import("../modules/UserTestimonialModule").then((m) => ({
    default: m.UserTestimonialModule,
  }))
);
const CleanSection = lazy(() =>
  import("../features/pdp/CleanSection").then((m) => ({
    default: m.CleanSection,
  }))
);
export { setCartAndCheckout } from "../services/shopifyCart";
export { getUTMAttributes } from "../helpers/utm";
export { generateRechargeUrl } from "../features/portal/RechargeApiActions";
export { getProduct } from "../helpers/droppsApp";

initSentry();
initializeAnnouncementBar();

function getCookie(name: string) {
  let cookieArray = document.cookie.split(";");
  for (let i = 0; i < cookieArray.length; i++) {
    let cookiePair = cookieArray[i].trim().split("=");
    if (cookiePair[0] === name) {
      return cookiePair[1];
    }
  }
  return null;
}

const Pdp = ({
  product,
  scents,
  cartAddId,
  pdpMetaData,
  updateScentUrl,
}: PdpFlowProps) => {
  return (
    <PdpFlow
      product={product}
      scents={scents}
      cartAddId={cartAddId}
      pdpMetaData={pdpMetaData}
      updateScentUrl={updateScentUrl}
    />
  );
};

export const renders: ({ id: string } & RenderKind)[] = [];

// Drain queue from inline section scripts that ran before this deferred bundle loaded
if (
  typeof window !== "undefined" &&
  (window as unknown as { __reactRenderQueue?: unknown[] }).__reactRenderQueue
) {
  const queue = (window as unknown as { __reactRenderQueue: unknown[] })
    .__reactRenderQueue;
  (window as unknown as { __reactRenderQueue: unknown[] }).__reactRenderQueue =
    [];
  queue.forEach((item: unknown) => {
    const entry = item as {
      __pdp?: boolean;
      getProductArgs?: Parameters<typeof getProduct>[0];
      payload?: Record<string, unknown>;
    };
    if (entry?.__pdp && entry.getProductArgs != null && entry.payload) {
      const [product, scents] = getProduct(entry.getProductArgs);
      renders.push({ ...entry.payload, product, scents } as {
        id: string;
      } & RenderKind);
    } else if (entry && !entry.__pdp) {
      renders.push(entry as { id: string } & RenderKind);
    }
  });
}

const PortalComponent = (render: RenderKind) => {
  switch (render.kind) {
    case "nav-view":
      return <NavView {...trimPortalProps(render)} />;
    case "pdp":
      return <Pdp {...trimPortalProps(render)} />;
    case "user-testimonials":
      return <UserTestimonialModule {...trimPortalProps(render)} />;
    case "pdp-clean-section":
      return <CleanSection {...trimPortalProps(render)} />;
    default:
      return <></>;
  }
};

const DroppsApp = () => {
  // Whenever someone pushes a new render, force react to re-render
  const [count, forceUpdate] = useReducer((x) => x + 1, 0);
  if (count === 0) {
    renders.push = (...args) => {
      const ret = Array.prototype.push.call(renders, ...args);
      forceUpdate();
      return ret;
    };
  }

  const mobile: boolean = useMediaQuery(
    theme.breakpoints.down(theme.breakpoints.values.lg)
  );

  return (
    <>
      {renders.map((x) => {
        const el = document.getElementById(x.id);
        if (!el) {
          throw new Error(`expected id ${x.id} to exist`);
        }
        const key = `${x.id}-${x.kind}`;
        return createPortal(
          <Sentry.ErrorBoundary showDialog>
            <Suspense fallback={null}>
              <PortalComponent {...x} />
            </Suspense>
          </Sentry.ErrorBoundary>,
          el,
          key
        );
      })}
    </>
  );
};

export const render = ({ user }: { user: Customer | null }) => {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      name: `${user.firstName} ${user.lastName}`,
    });
  }

  const container = document.getElementById("dropps-app-root");
  if (container === null) {
    throw new Error("dropps-app-root not found");
  }

  const root = createRoot(container);
  root.render(
    <React.StrictMode>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            <CustomerProvider customer={user}>
              <GrowthBookProvider growthbook={growthBook}>
                <CartProvider>
                  <DroppsApp />
                </CartProvider>
              </GrowthBookProvider>
            </CustomerProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </React.StrictMode>
  );
};
